import { enforceResourceNameLength, isRequireCommentsEnabled } from '@gonfalon/dogfood-flags/dogfood-flags';
// oxlint-disable-next-line no-restricted-imports
import { fromJS, Map, Record, Set } from '@gonfalon/immutable-shim/immutableShim';
import { createColor } from '@gonfalon/legacy-color/colorUtils';
import {
  isInRange,
  isLength,
  isNotEmpty,
  isReservedKey,
  isValidTagList,
  validateRecord,
} from '@gonfalon/legacy-form-validation/validationUtils';
import type { CreateFunctionInput, ImmutableMap } from '@gonfalon/legacy-immutable/immutableUtils';

import { FieldIds, getFieldPrefix } from '#components/ResourceApprovalSettings/approvalSettingsUtils';
import { allowDecision } from '#utils/access/allowDecision';
import { createAccessDecision } from '#utils/access/records/AccessDecision';
import { MAX_ENVIRONMENT_KEY_LENGTH, MAX_ENVIRONMENT_NAME_LENGTH } from '#utils/environments/constants';
import { createApprovalRequestSettings } from '#utils/environments/records/ApprovalSettings';
import { PubNub } from '#utils/environments/records/PubNub';
import type { ResourceApprovalSettings } from '#utils/environments/records/ResourceApprovalSettings';
import { createResourceApprovalRequestSettings } from '#utils/environments/records/ResourceApprovalSettings';
import type { EnvironmentProps } from '#utils/environments/types';
import { ApprovalRequirementStrategy, ApprovalResourceKind, ApprovalServiceKind } from '#utils/environments/types';
import type { Member } from '#utils/members/records/Member';

export class Environment extends Record<EnvironmentProps>({
  defaultTrackEvents: false,
  _access: undefined,
  _links: Map(),
  _id: '',
  _pubnub: undefined,
  _followed: false,
  key: '',
  apiKey: '',
  mobileKey: '',
  name: '',
  color: createColor({ h: 0, s: 1, l: 0.5 }),
  defaultTtl: 0,
  secureMode: false,
  approvalSettings: undefined,
  resourceApprovalSettings: undefined,
  tags: Set(),
  requireComments: false,
  confirmChanges: false,
  critical: false,
}) {
  checkAccess({ profile }: { profile: Member }) {
    const access = this._access;

    if (profile.isReader()) {
      return () => createAccessDecision({ isAllowed: false, appliedRoleName: 'Reader' });
    }

    if (profile.hasStrictWriterRights() || !access || !access.get('denied')) {
      return allowDecision;
    }

    return (action?: string) => {
      const deniedAction = access.get('denied').find((v) => v.get('action') === action);
      if (deniedAction) {
        const reason = deniedAction.get('reason');
        const roleName = reason && reason.get('role_name');
        return createAccessDecision({
          isAllowed: false,
          appliedRoleName: roleName,
        });
      }
      return createAccessDecision({ isAllowed: true });
    };
  }

  selfLink() {
    return this.getIn(['_links', 'self', 'href']);
  }

  validate() {
    return validateRecord(
      this,
      isNotEmpty('name'),
      enforceResourceNameLength() ? isLength(1, MAX_ENVIRONMENT_NAME_LENGTH)('name') : () => undefined,
      isNotEmpty('key'),
      isReservedKey('key'),
      isLength(1, MAX_ENVIRONMENT_KEY_LENGTH)('key'),
      isNotEmpty('defaultTtl'),
      isInRange({ min: 0, max: 60 })('defaultTtl'),
      isNotEmpty('color'),
      isValidTagList('tags'),
      (env) => {
        const validationResults = [] as Array<{ for: string; errors: string[] }>;

        for (const value of Object.values<ResourceApprovalSettings & { requiredApprovalTags: string[] }>(
          env.resourceApprovalSettings ?? [],
        )) {
          if (
            value.resourceKind === ApprovalResourceKind.SEGMENT &&
            value.approvalRequirementStrategy === ApprovalRequirementStrategy.BY_TAG &&
            value.requiredApprovalTags.length === 0
          ) {
            validationResults.push({
              for: `${getFieldPrefix(value.resourceKind)}${FieldIds.REQUIRED_APPROVAL_TAGS_SELECT}`,
              errors: ['Select one or more tags'],
            });
          }
        }

        if (
          env.approvalSettings?.approvalRequirementStrategy === ApprovalRequirementStrategy.BY_TAG &&
          env.approvalSettings?.requiredApprovalTags.length === 0
        ) {
          validationResults.push({
            for: `${getFieldPrefix(ApprovalResourceKind.FLAG)}${FieldIds.REQUIRED_APPROVAL_TAGS_SELECT}`,
            errors: ['Select one or more tags'],
          });
        }

        if (
          this.hasIntegrationApproval() &&
          env.approvalSettings?.serviceKind === ApprovalServiceKind.ServiceNowApprovalServiceKind &&
          (!env.approvalSettings?.serviceConfig || !env.approvalSettings?.serviceConfig.template)
        ) {
          validationResults.push({ for: 'config.template', errors: ['Change template is required'] });
        }

        return validationResults;
      },
    );
  }

  isNew() {
    return !this._id;
  }

  usesLaunchDarklyApprovalService() {
    return this.approvalSettings?.serviceKind === ApprovalServiceKind.LaunchDarklyApprovalServiceKind;
  }

  hasIntegrationApproval() {
    return (
      !!this.approvalSettings?.serviceKind &&
      this.approvalSettings?.serviceKind !== ApprovalServiceKind.LaunchDarklyApprovalServiceKind
    );
  }

  toRep(): ImmutableMap<Omit<EnvironmentProps, 'color'> & { color: string }> {
    return fromJS(this.toJSON()).withMutations((map: ImmutableMap<EnvironmentProps>) => {
      map.set('color', this.color.hex());
      map.update('approvalSettings', (approvalSettings) => approvalSettings?.toRep());
      map.update('resourceApprovalSettings', (settings) => settings?.map((value) => value.toRep()));
    });
  }
}

export function createEnvironment(props?: CreateFunctionInput<Environment>) {
  const env = props instanceof Environment ? props : new Environment(fromJS(props));
  const color = props?.color ? () => createColor(props?.color as string) : createColor;

  return env.withMutations((e) => {
    e.update('color', color);
    e.update('_pubnub', (p) => new PubNub(p));
    e.update('tags', (tags) => (tags ? tags.toSet() : Set()));
    e.update('approvalSettings', createApprovalRequestSettings);
    e.update('resourceApprovalSettings', (settings) => {
      let resourceApprovalSettings = Map<ApprovalResourceKind, ResourceApprovalSettings>();

      Object.entries(ApprovalResourceKind).forEach(([, resourceKind]) => {
        const approvalSettings = createResourceApprovalRequestSettings(settings?.get(resourceKind));
        resourceApprovalSettings = resourceApprovalSettings.set(resourceKind, approvalSettings);
      });

      return resourceApprovalSettings;
    });
    e.update('requireComments', (requireComments) => (isRequireCommentsEnabled() ? requireComments : false));
  });
}
